import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "@/components/site/Reveal";
import {
  Waveform,
  Lightning,
  ChartLineUp,
  Wrench,
  ShieldCheck,
  Anchor,
  ArrowUpRight,
} from "@phosphor-icons/react";

const IMG = {
  wireline:
    "https://images.unsplash.com/photo-1650698877967-734f036a0c08?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  rig:
    "https://images.unsplash.com/photo-1563118351-26b2c7381731?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const SERVICES = [
  {
    id: "cased-hole",
    n: "01",
    title: "Cased-Hole Logging",
    icon: Waveform,
    desc: "Cement evaluation, corrosion and saturation logging through casing to verify well condition after completion.",
    img: IMG.wireline,
  },
  {
    id: "perforating",
    n: "02",
    title: "Perforating",
    icon: Lightning,
    desc: "Precise depth-correlated perforating gun conveyance to open the reservoir to the wellbore, on demand.",
    img: IMG.rig,
  },
  {
    id: "production-logging",
    n: "03",
    title: "Production Logging",
    icon: ChartLineUp,
    desc: "Flow profiling, phase discrimination and downhole diagnostics to understand exactly what each zone produces.",
    img: IMG.wireline,
  },
  {
    id: "pipe-recovery",
    n: "04",
    title: "Pipe Recovery",
    icon: Wrench,
    desc: "Free-point, back-off and severing operations to recover stuck pipe and get the well back on program.",
    img: IMG.rig,
  },
  {
    id: "well-integrity",
    n: "05",
    title: "Well Integrity",
    icon: ShieldCheck,
    desc: "Barrier verification, leak detection and multi-finger caliper surveys that keep the well safe over its life.",
    img: IMG.wireline,
  },
  {
    id: "offshore-ops",
    n: "06",
    title: "Offshore Operations",
    icon: Anchor,
    desc: "Full rig-up on floaters, jack-ups and platforms — pressure control, deployment and logging in any approved sea state.",
    img: IMG.rig,
  },
];

function ServiceCell({ s, hovered, setHovered }) {
  const Icon = s.icon;
  const active = hovered === s.id;
  return (
    <div
      className="relative border-b border-r border-white/10 p-8 md:p-10 min-h-[300px] flex flex-col justify-between overflow-hidden group cursor-default"
      onMouseEnter={() => setHovered(s.id)}
      onMouseLeave={() => setHovered(null)}
      data-testid={`service-${s.id}`}
    >
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 -z-0"
          >
            <img
              src={s.img}
              alt={s.title}
              className="h-full w-full object-cover grayscale"
            />
            <div className="absolute inset-0 bg-background/80" />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative flex items-start justify-between">
        <Icon
          size={40}
          weight="light"
          className={`transition-colors duration-300 ${
            active ? "text-primary" : "text-foreground/70"
          }`}
        />
        <span className="font-mono-tech text-sm text-muted-foreground">{s.n}</span>
      </div>

      <div className="relative">
        <h3 className="font-display font-bold uppercase tracking-tight text-2xl md:text-3xl mb-4 flex items-center gap-2">
          {s.title}
          <ArrowUpRight
            size={22}
            weight="bold"
            className={`transition-all duration-300 ${
              active ? "opacity-100 text-primary" : "opacity-0 -translate-x-2"
            }`}
          />
        </h3>
        <p className="text-foreground/60 text-sm md:text-base leading-relaxed max-w-md">
          {s.desc}
        </p>
      </div>
    </div>
  );
}

export default function Services() {
  const [hovered, setHovered] = useState(null);
  return (
    <section
      id="services"
      className="relative bg-background border-b border-white/10"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 pt-24 md:pt-32 pb-12">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <span className="mono-label text-primary">/ Capabilities</span>
            <h2 className="mt-6 font-display font-bold uppercase tracking-tight text-4xl md:text-6xl leading-[0.9]">
              What we run
              <br />
              downhole
            </h2>
          </div>
          <p className="max-w-sm text-foreground/60 md:pb-2">
            A complete cased-hole and slickline service line, engineered for offshore
            deployment and delivered by certified field crews.
          </p>
        </Reveal>
      </div>

      <div className="mx-auto max-w-[1600px] px-5 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
          {SERVICES.map((s) => (
            <ServiceCell key={s.id} s={s} hovered={hovered} setHovered={setHovered} />
          ))}
        </div>
      </div>
    </section>
  );
}
